import { useEffect } from "react";import "@/App.css";
import { Toaster } from "sonner";
import PublicPreview from "@/components/PublicPreview";
import FunctionallyScaffoldedComposition from "@/components/FunctionallyScaffoldedComposition";
import EssayComposition from "@/components/EssayComposition";
import TeacherReview from "@/components/TeacherReview";
import PortalHome from "@/components/PortalHome";
import HowItWorks from "@/components/HowItWorks";
import HowStrengthenWorks from "@/components/HowStrengthenWorks";
import StudentPathway from "@/components/StudentPathway";
import EpisodeReview from "@/components/EpisodeReview";
import PreviewBridge from "@/components/PreviewBridge";
import ResetPassword from "@/components/ResetPassword";
import AcceptInvite from "@/components/AcceptInvite";
import AdminInvites from "@/components/AdminInvites";
import ClassroomApp from "@/components/ClassroomApp";
import { AccessGate } from "@/components/AccessGate";
import { VerifyEmailNotice, VerifyEmailLanding } from "@/components/VerifyEmail";
import { useAuth } from "@/context/AuthContext";
import FeedbackDashboard from "@/components/FeedbackDashboard";
import TeacherReviewPublic from "@/components/TeacherReviewPublic";

const STORAGE_KEY = "dws_session_id";

let _freshHandled = false;   // ?fresh=1 start-fresh cleanup runs once per page load

function App() {
  const { user: authUser } = useAuth();
  // SILENT KEEP-ALIVE HEARTBEAT — prevents the preview environment from idling out ("waking up servers")
  // during long reading/thinking pauses in a session. While the tab is open it quietly GETs /api/ every 45s
  // so the idle timer keeps resetting. Deliberately silent: raw fetch (bypasses axios toasts/interceptors),
  // no UI, no sound, no console output; all failures swallowed. Placed before any route return so it runs
  // on every route and mounts exactly once (App is the root and never unmounts).
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return undefined;
    const ping = () => {
      try { fetch(`${base}/api/`, { method: "GET", cache: "no-store", keepalive: true }).catch(() => {}); }
      catch (e) { /* silent */ }
    };
    const id = setInterval(ping, 45000);
    return () => clearInterval(id);
  }, []);

  // Developer-only automated instructional testing harness (?tests). Decided at
  // the top-level wrapper so hook order in the studio app is never affected.
  const params = new URLSearchParams(window.location.search);
  // PREVIEW/TEST START-FRESH: `?fresh=1` (or `?new=1`) on ANY route (incl. the bare portal `/`) drops the
  // client-side student-session resume pointers BEFORE any route mounts, so a returning guest lands clean.
  // Guarded to run ONCE per page load (module flag) so it never wipes a session the student creates afterward.
  if (!_freshHandled && (params.get("fresh") === "1" || params.get("new") === "1")) {
    _freshHandled = true;
    try {
      localStorage.removeItem("compass_student_session");
      localStorage.removeItem("compass_ot_session");
    } catch (e) { /* ignore */ }
    // the flag has done its work: drop it from the address bar so a RELOAD resumes the fresh session instead of starting another
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("fresh"); u.searchParams.delete("new");
      window.history.replaceState(null, "", u.toString());
    } catch (e) { /* ignore */ }
  }
  // PHASE 2C — CONTROLLED PREVIEW ACTIVATION. `?tws2c=1` turns ON adaptive structural scaffolding (the Phase-2B
  // decision actually controls what the map externalizes) for THIS browser; `?tws2c=0` turns it off. Persisted
  // so the ordinary student flow (open URL -> use TWS) stays in 2C without needing the shadow inspector. This is
  // Preview-only: without the flag every student behaves exactly as production.
  try {
    if (params.get("tws2c") === "1") localStorage.setItem("tws_phase2c", "1");
    else if (params.get("tws2c") === "0") localStorage.removeItem("tws_phase2c");
    // CONSOLIDATED TEACHING LOOP — the Full Studio's default pathway (appraiser -> controller -> ACT; sticky per session on the
    // server). `?loop=old` opts this browser back onto the old pathway (kept intact); `?loop=new` returns to the default.
    if (params.get("loop") === "new") localStorage.setItem("tws_new_loop", "1");
    else if (params.get("loop") === "old") localStorage.setItem("tws_new_loop", "0");
  } catch (e) { /* ignore */ }
  // Password reset landing (?reset=<token>) — takes precedence over all other routes.
  const verifyToken = params.get("verify");
  if (verifyToken) {
    return <VerifyEmailLanding token={verifyToken} />;
  }
  // SIGNED IN BUT NOT CONFIRMED — the backend refuses every protected address; show the one screen that helps.
  if (authUser && authUser.email_verified === false) {
    return <VerifyEmailNotice />;
  }
  const resetToken = params.get("reset");
  if (resetToken) {
    return <ResetPassword token={resetToken} />;
  }
  // Beta access: invitation landing (?invite=<token>) and the minimal admin teacher-invitation control
  // (?admin-invites). Both are auth exits, like ?reset — they work on every deployment mode.
  const inviteToken = params.get("invite");
  if (inviteToken) {
    return <AcceptInvite token={inviteToken} />;
  }
  if (params.has("admin-invites")) {
    return <AdminInvites />;
  }
  // ── DEDICATED DEPLOYMENT MODE: REACT_APP_PUBLIC_MODE=teacher_review ──────────
  // When this build is deployed with REACT_APP_PUBLIC_MODE=teacher_review, the HOST
  // exposes ONLY the bounded Teacher Review experience: every route renders the
  // Teacher Review shell (face page at /, demo at /review) EXCEPT the intentional
  // auth exit (?classroom / ?reset). This is a boot/routing boundary enforced by
  // the deployment env var — not a sessionStorage pin or JS redirect. On the normal
  // deployment (flag unset) the app behaves exactly as before.
  if (process.env.REACT_APP_PUBLIC_MODE === "teacher_review" && !params.has("classroom") && !params.has("reset")) {
    return <TeacherReviewPublic />;
  }
  // ── BOUNDED PUBLIC TEACHER REVIEW ENTRY (shared preview host) ────────────────
  // Dedicated path (/teacher-review) OR legacy ?teacher-review-demo. Once a visitor
  // enters this bounded shell we PIN it for the browser tab (sessionStorage), so
  // shortening/editing the URL (e.g. back to "/") cannot sideways-navigate into the
  // general TWS experience — it returns to the Teacher Review landing instead. The
  // only intentional outbound destination is the auth hub (Sign in / Sign up).
  const path = window.location.pathname || "/";
  const enteringTR = path.startsWith("/teacher-review") || params.has("teacher-review-demo");
  // ── PLAIN PORTAL PATHS (no suffixes): /teacher and /admin → the educator dashboard (an administrator lands on Admin) · /student → the student
  // experience (also "Student view" for educators). Sign-in lives inside each; a signed-in visitor at "/" is routed by role (PortalHome).
  const portalPath = path.replace(/\/+$/, "");
  // HOW IT WORKS — the public step-through preview of the studio (no account needed).
  if (portalPath === "/how-it-works") return <HowItWorks />;
  if (portalPath === "/how-strengthen-works") return <HowStrengthenWorks />;
  if (portalPath === "/how-students-start") return <HowStrengthenWorks tour="student" />;
  if (portalPath === "/how-teachers-work") return <HowStrengthenWorks tour="teacher" />;
  const isPortalPath = portalPath === "/teacher" || portalPath === "/admin" || portalPath === "/student";
  const isAuthRoute = params.has("classroom") || params.has("reset") || isPortalPath;
  if (enteringTR) {
    try { sessionStorage.setItem("tws_bounded_tr", "1"); } catch (_) {}
    return <TeacherReviewPublic />;
  }
  if (isPortalPath) {
    if (portalPath === "/student") return <PortalHome initialPortal="student" />;
    return (<><Toaster position="top-center" richColors /><ClassroomApp /></>);
  }
  if (sessionStorage.getItem("tws_bounded_tr") === "1" && !isAuthRoute) {
    if (path !== "/teacher-review") window.history.replaceState(null, "", "/teacher-review");
    return <TeacherReviewPublic />;
  }
  // Teacher Review dashboard (admin) — in-context learner feedback.
  if (params.has("teacher-review")) {
    return <AccessGate level="admin"><FeedbackDashboard /></AccessGate>;
  }
  // A student's join link from their teacher's invitation email (?join=CODE): sign in / create the student account, then the code is entered for them.
  const joinCode = params.get("join");
  if (joinCode) {
    return <PortalHome initialPortal="student" initialJoin={joinCode.toUpperCase()} />;
  }
  // Classroom (Phase 1) — Teacher/Student dashboard spine (?classroom). ?classroom=student opens My Classes for anyone with student access
  // (including an educator whose account also holds the student role).
  if (params.has("classroom")) {
    return (<><Toaster position="top-center" richColors /><ClassroomApp forceStudent={params.get("classroom") === "student"} /></>);
  }
  // CANONICAL PUBLIC EXPERIENCE (?experience) — the one public path: the two-round bounded writing
  // experience (Assignment → Composition → Visual Map → Ready to Wrap → Sentence Craft → Review). The
  // public user never sees preview/two_round/dco concepts; they are wired on here.
  if (params.has("experience")) {
    return (<AccessGate><Toaster position="top-center" richColors /><PublicPreview mode="writing" /></AccessGate>);
  }
  // ── STUDENT ENTRY (product-facing) — the four official entry points ──────────
  //   ?studio             → Student pathway choice ("How would you like to work?")
  //   ?studio=full        → FULL STUDENT EXPERIENCE (Assignment → OT/My Thinking → FSC → Composition …)
  //   ?studio=composition → COMPOSITION ONLY (direct entry at the shared Composition engine)
  // Both pathways CONVERGE on the one Composition engine (PublicPreview). This is the intended
  // product architecture; the internal ?preview=ot / ?preview=writing dev flags remain for testing
  // and are unchanged. The general Landing at "/" (default/production) is untouched.
  if (params.has("studio")) {
    const s = params.get("studio");
    if (s === "full") {
      return (<AccessGate><Toaster position="top-center" richColors /><PublicPreview mode="ot" /></AccessGate>);
    }
    if (s === "composition") {
      return (<AccessGate><Toaster position="top-center" richColors /><PublicPreview mode="writing" /></AccessGate>);
    }
    return (
      <StudentPathway
        onChoose={(choice) => {
          window.location.search = choice === "composition" ? "?studio=composition" : "?studio=full";
        }}
      />
    );
  }
  // Public Preview / student entries (?preview).
  //   ?preview=writing → genuine STUDENT Composition entry (student-facing)
  //   ?preview=ot      → genuine STUDENT Organizing Thought entry (student-facing)
  //   ?preview=teacher → teacher-simulation wrapper (Welcome + create-assignment + teacher reflection)
  //   ?preview         → default: genuine STUDENT Organizing Thought (never the teacher simulation)
  if (params.has("preview")) {
    const p = params.get("preview");
    // FUNCTIONALLY SCAFFOLDED COMPOSITION (fsc) — Prototype 1 dev entry. Seeds/loads a real OT session
    // and opens the single developing writing space (thesis inside, My Thinking available, TWS controller).
    if (p === "fsc") {
      return (<AccessGate><Toaster position="top-center" richColors /><FunctionallyScaffoldedComposition sessionId={params.get("sid") || null} /></AccessGate>);
    }
    // ESSAY-LEVEL COMPOSITION CONTROLLER — dev entry to exercise the whole-first controller on a seeded
    // session (?preview=essay&sid=<id>). Production reaches it via ?studio (OT→FSC→essay / comp-only).
    if (p === "essay") {
      return (<AccessGate><Toaster position="top-center" richColors /><EssayComposition sessionId={params.get("sid")} /></AccessGate>);
    }
    const mode = p === "writing" ? "writing" : p === "teacher" ? "teacher" : "ot";
    // ORGANIZING THINKING (ot) is RE-EXPOSED for development in Preview: `?preview=ot` opens the real OT
    // workspace. The full standalone Composition (writing) mode stays gated — its public path is the
    // bounded ?experience flow.
    if (mode === "writing") {
      return <ComingSoonGate />;
    }
    return (<AccessGate><Toaster position="top-center" richColors /><PublicPreview mode={mode} /></AccessGate>);
  }
  // Teacher Review (?review) — inspect the frozen engine on precomputed cases.
  if (params.has("review")) {
    return <TeacherReview />;
  }
  // Two-round cumulative Teacher Review for a specific session (?episodereview=<session_id>).
  if (params.has("episodereview")) {
    const sid = params.get("episodereview") || localStorage.getItem(STORAGE_KEY);
    return <AccessGate><EpisodeReview sessionId={sid} onBack={() => { window.location.search = ""; }} /></AccessGate>;
  }
  // Preview → Real bridge (?bridge) — reachable standalone (product map S3).
  if (params.has("bridge")) {
    return <AccessGate><PreviewBridge sessionId={null} /></AccessGate>;
  }
  // Production home — the simple Student Portal / Educator Portal page, for EVERY unmatched URL
  // (including flag-only URLs such as ?testmode=1). The old marketing "Philosophy" landing page is retired.
  return <PortalHome />;
}

function ComingSoonGate() {
  return (
    <div data-testid="coming-soon-gate" className="min-h-screen flex flex-col items-center justify-center bg-[#faf9f6] px-6 text-center">
      <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-3">Coming soon</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 mb-3 max-w-xl leading-tight">
        This mode is still under development
      </h1>
      <p className="text-[15px] text-stone-600 max-w-md mb-8">
        The full Organizing and Composition modes aren't open to the public yet. In the meantime, you can
        try the guided Experience The Writing Studio.
      </p>
      <a
        href="?experience"
        data-testid="coming-soon-experience-link"
        className="inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-7 py-3.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-[#a34635] transition-colors"
      >
        Experience The Writing Studio
      </a>
    </div>
  );
}

export default App;
